import React, { useState, useEffect, useCallback } from 'react';
import { dispositionsAPI } from '../services/api';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiDownload } from 'react-icons/fi';
import toast from 'react-hot-toast';

const PALETTE = ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4', '#6b7280', '#ec4899'];

export default function DispositionsPage() {
  const [codes, setCodes] = useState([]);
  const [breakdown, setBreakdown] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null); // {} for new, {id,...} for edit
  const [range, setRange] = useState({ start_date: '', end_date: '' });
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (range.start_date) params.start_date = range.start_date;
      if (range.end_date) params.end_date = range.end_date;
      const [c, s] = await Promise.all([dispositionsAPI.codes(), dispositionsAPI.stats(params).catch(() => null)]);
      setCodes(c.data.codes || []);
      if (s) setBreakdown(s.data.breakdown || []);
    } catch { toast.error('Xato'); }
    finally { setLoading(false); }
  }, [range.start_date, range.end_date]);

  useEffect(() => { load(); }, [load]);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const params = {};
      if (range.start_date) params.start_date = range.start_date;
      if (range.end_date) params.end_date = range.end_date;
      const res = await dispositionsAPI.exportOutcomes(params);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.ms-excel' }));
      const a = document.createElement('a');
      a.href = url;
      const stamp = `${range.start_date || 'boshdan'}_${range.end_date || 'oxirigacha'}`;
      a.download = `natijalar_${stamp}.xls`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Eksport qilishda xato');
    } finally {
      setExporting(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`«${c.name}» nofaol qilinsinmi?`)) return;
    try { await dispositionsAPI.delete(c.id); load(); } catch { toast.error('Xato'); }
  };

  const totalOutcomes = breakdown.reduce((s, b) => s + b.count, 0);

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Natija kodlari</h1>
          <p className="page-subtitle">Qo'ng'iroq yakuni kodlari va taqsimot</p>
        </div>
        <button className="btn btn-primary" onClick={() => setEdit({})}><FiPlus size={16} /> Kod</button>
      </div>

      {/* Sana oralig'i + Excel eksport */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Boshlanish sanasi</label>
            <input className="input" type="date" value={range.start_date}
              max={range.end_date || undefined}
              onChange={e => setRange(p => ({ ...p, start_date: e.target.value }))} />
          </div>
          <div>
            <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Tugash sanasi</label>
            <input className="input" type="date" value={range.end_date}
              min={range.start_date || undefined}
              onChange={e => setRange(p => ({ ...p, end_date: e.target.value }))} />
          </div>
          {(range.start_date || range.end_date) && (
            <button className="btn btn-ghost" onClick={() => setRange({ start_date: '', end_date: '' })}>Tozalash</button>
          )}
          <button className="btn btn-primary" onClick={exportExcel} disabled={exporting} style={{ marginLeft: 'auto' }}>
            <FiDownload size={16} /> {exporting ? 'Yuklanmoqda...' : "Excel'ga yuklab olish"}
          </button>
        </div>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '10px 0 0' }}>
          Faqat natija (yakun kodi) belgilangan qo'ng'iroqlar eksport qilinadi.
          {(!range.start_date && !range.end_date) && ' Sana tanlanmasa — barcha davr.'}
        </p>
      </div>

      {/* Breakdown */}
      {totalOutcomes > 0 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header"><span className="card-title">Taqsimot (jami {totalOutcomes})</span></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {breakdown.filter(b => b.count > 0).map(b => {
              const pct = Math.round((b.count / totalOutcomes) * 100);
              return (
                <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ width: 120, fontSize: 13, color: b.color, fontWeight: 600 }}>{b.name}</span>
                  <div style={{ flex: 1, height: 10, background: 'var(--bg-primary,#0f172a)', borderRadius: 6, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: b.color }} />
                  </div>
                  <span style={{ width: 70, textAlign: 'right', fontSize: 13, color: 'var(--text-muted)' }}>{b.count} · {pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Codes table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="table-wrapper"><table><thead><tr>
          <th>Nom</th><th>Rang</th><th>Avto follow-up</th><th>Holat</th><th style={{ width: 90 }}>Amallar</th>
        </tr></thead><tbody>
          {codes.map(c => (
            <tr key={c.id} style={c.is_active ? undefined : { opacity: 0.5 }}>
              <td style={{ fontWeight: 600 }}>
                <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: c.color, marginRight: 8 }} />
                {c.name}
              </td>
              <td><span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)' }}>{c.color}</span></td>
              <td>{c.creates_followup ? <span className="badge badge-info">Ha</span> : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
              <td>{c.is_active ? <span className="badge badge-success">Faol</span> : <span className="badge badge-neutral">Nofaol</span>}</td>
              <td>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button className="btn btn-ghost btn-icon" onClick={() => setEdit(c)} title="Tahrirlash"><FiEdit2 size={14} /></button>
                  {c.is_active && <button className="btn btn-ghost btn-icon" onClick={() => remove(c)} title="Nofaol qilish" style={{ color: '#ef4444' }}><FiTrash2 size={14} /></button>}
                </div>
              </td>
            </tr>
          ))}
        </tbody></table></div>
      </div>

      {edit && <CodeForm code={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function CodeForm({ code, onClose, onSaved }) {
  const isNew = !code.id;
  const [form, setForm] = useState({
    name: code.name || '', color: code.color || '#3b82f6',
    sort_order: code.sort_order || 0, creates_followup: !!code.creates_followup,
    is_active: code.is_active !== undefined ? code.is_active : true,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error('Nom majburiy'); return; }
    setSaving(true);
    try {
      if (isNew) await dispositionsAPI.create(form);
      else await dispositionsAPI.update(code.id, form);
      toast.success('Saqlandi');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.error || 'Xato'); }
    finally { setSaving(false); }
  };

  return (
    <div style={ov} onClick={onClose}>
      <div style={md} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>{isNew ? 'Yangi kod' : 'Kodni tahrirlash'}</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><FiX size={18} /></button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <input className="input" placeholder="Nom *" value={form.name} onChange={e => set('name', e.target.value)} />
          <div>
            <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Rang</label>
            <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
              {PALETTE.map(col => (
                <button key={col} onClick={() => set('color', col)} title={col}
                  style={{ width: 28, height: 28, borderRadius: 8, background: col, cursor: 'pointer',
                    border: form.color === col ? '3px solid var(--text-primary)' : '2px solid transparent' }} />
              ))}
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.creates_followup} onChange={e => set('creates_followup', e.target.checked)} />
            Avtomatik follow-up vazifasi yaratsin
          </label>
          {!isNew && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} />
              Faol
            </label>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ fontSize: 14 }}>Tartib:</label>
            <input className="input" type="number" style={{ width: 90 }} value={form.sort_order} onChange={e => set('sort_order', parseInt(e.target.value) || 0)} />
          </div>
          <button className="btn btn-primary" onClick={save} disabled={saving} style={{ marginTop: 4 }}>
            {saving ? 'Saqlanmoqda...' : 'Saqlash'}
          </button>
        </div>
      </div>
    </div>
  );
}

const ov = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(3px)' };
const md = { background: 'var(--bg-secondary,#1e293b)', border: '1px solid var(--border,#2d3a4f)', borderRadius: 16, padding: 24, width: 400, maxWidth: '92vw' };
